import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Group,
  Modal,
  ScrollArea,
  Text,
  TextInput,
  Timeline,
  Title,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconHistory } from "@tabler/icons-react";
import QueryString from "qs";
import { useState } from "react";
import toast from "react-hot-toast";
import useSWR from "swr";
import ListRow from "@/components/ListRow";
import Empty from "@/components/placeholders/Empty";
import useAuthStore from "@/store/useAuthStore";
import { parseMoney } from "@/utils/helpers";

export default function RecentCargoModal({ form, companyId }) {
  const [opened, { open, close }] = useDisclosure();
  const isMobile = useMediaQuery("(max-width: 720px)");
  const user = useAuthStore((state) => state.user);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const params = QueryString.stringify({
    filters: {
      company: companyId,
      status: {
        $notIn: ["canceled", "feeCanceled"],
      },
      $or: [
        {
          startAddress: {
            $contains: searchQuery || undefined,
          },
        },
        {
          startName: {
            $contains: searchQuery || undefined,
          },
        },
        {
          endAddress: {
            $contains: searchQuery || undefined,
          },
        },
        {
          endName: {
            $contains: searchQuery || undefined,
          },
        },
      ],
    },
    sort: ["createdAt:desc"],
    populate: "productImages",
    pagination: {
      page: 1,
      pageSize: 50,
    },
  });

  const { data, isLoading } = useSWR(opened ? ["/orders", params] : null);

  function handleSelect(item) {
    form.setValues({
      startName: item.startName,
      startAddress: item.startAddress,
      startAddressDetail: item.startAddressDetail,
      startLat: item.startLat,
      startLng: item.startLng,
      startPhoneNumber: item.startPhoneNumber,

      endName: item.endName,
      endAddress: item.endAddress,
      endAddressDetail: item.endAddressDetail,
      endLat: item.endLat,
      endLng: item.endLng,
      endPhoneNumber: item.endPhoneNumber,

      // productWeight: item.productWeight,
      productContent: item.productContent,
      // carType: item.carType,
      // carWeight: item.carWeight,
      loadType: item.loadType,
      unloadType: item.unloadType,
      // isReceiptRequired: item.isReceiptRequired,
      // isRoundTrip: item.isRoundTrip,
      // isMixed: item.isMixed,
      startType: "",
      endType: "",
      productItem: item?.productImages?.data?.[item?.productImages?.data?.length - 1] || {},
      isFeeOrder: false,
    });

    toast.success("화물이 선택되었습니다\n 상/하차 일시를 확인해주세요");
    close();
  }

  return (
    <>
      <Button onClick={open} leftIcon={<IconHistory size={18} />}>
        화물 불러오기
      </Button>

      <Modal.Root opened={opened} onClose={close} centered size="lg">
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header sx={{ borderBottom: "1px solid lightgray" }}>
            <Box w="100%">
              <Group>
                <Modal.Title>최근 화물 불러오기</Modal.Title>
                <Modal.CloseButton />
              </Group>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setSearchQuery(search);
                }}
              >
                <Flex gap="xs" mt="sm">
                  <TextInput
                    style={{ flex: 1 }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="통합 검색 (상하차지명, 주소)"
                    mb={0}
                  />
                  <Flex gap="xs">
                    <Button
                      type="button"
                      onClick={() => {
                        setSearch("");
                        setSearchQuery("");
                      }}
                      variant="outline"
                    >
                      초기화
                    </Button>
                    <Button type="submit">검색</Button>
                  </Flex>
                </Flex>
              </form>
            </Box>
          </Modal.Header>

          <Modal.Body px={0}>
            <ScrollArea mih={500} px="sm">
              {!isLoading && data?.data?.length === 0 && (
                <Empty title="최근 등록된 화물이 없습니다" />
              )}

              {data?.data?.map((row) => {
                const item = row?.attributes || {};

                return (
                  <Card
                    key={row.id}
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleSelect(item)}
                    mt="sm"
                    py="xs"
                    shadow="0"
                    bg="gray.0"
                  >
                    <Grid align="center">
                      <Grid.Col xs={12} md={7}>
                        <Timeline bulletSize={16} lineWidth={3} my="xs" active={1}>
                          <Timeline.Item title={item?.startName}>
                            <Text size="xs" color="dimmed">
                              {item?.startAddress} {item?.startAddressDetail}
                            </Text>
                          </Timeline.Item>
                          <Timeline.Item mt={2} title={item?.endName}>
                            <Text size="xs" color="dimmed">
                              {item?.endAddress} {item?.endAddressDetail}
                            </Text>
                          </Timeline.Item>
                        </Timeline>
                      </Grid.Col>

                      {!isMobile && (
                        <Grid.Col xs={12} md={5}>
                          <ListRow cat="화물 중량(t)" value={`${item?.productWeight}t`} />
                          <ListRow cat="톤수" value={item?.carWeight} />
                          <ListRow cat="차종" value={item?.carType} isLast />
                        </Grid.Col>
                      )}
                    </Grid>

                    {isMobile && (
                      <Card.Section withBorder>
                        <Flex justify="space-between" p="sm">
                          <Box sx={{ flex: 1 }} ta="center">
                            <Title order={6}>{item?.productWeight}t</Title>
                            <Text size="xs" color="dimmed">
                              화물 중량
                            </Text>
                          </Box>

                          <Box sx={{ flex: 1 }} ta="center">
                            <Title order={6}>{item?.carWeight}</Title>
                            <Text size="xs" color="dimmed">
                              톤수
                            </Text>
                          </Box>

                          <Box sx={{ flex: 1 }} ta="center">
                            <Title order={6}>{item?.carType}</Title>
                            <Text size="xs" color="dimmed">
                              차종
                            </Text>
                          </Box>
                        </Flex>
                      </Card.Section>
                    )}
                    {(user?.permission === "admin" || user?.permission === "partner") && (
                      <Card.Section withBorder>
                        <Flex justify="space-between" px="sm" pb="sm" mt="sm">
                          <Box sx={{ flex: 1 }} ta="center">
                            <Title order={6}>{item?.isFeeOrder ? "선착불" : "인수증"}</Title>
                            <Text size="xs" color="dimmed">
                              지급방식
                            </Text>
                          </Box>
                          <Box sx={{ flex: 1 }} ta="center">
                            <Title order={6}>{parseMoney(item?.price)}</Title>
                            <Text size="xs" color="dimmed">
                              매출
                            </Text>
                          </Box>
                          <Box sx={{ flex: 1 }} ta="center">
                            <Title order={6}>{parseMoney(item?.driverPrice)}</Title>
                            <Text size="xs" color="dimmed">
                              매입
                            </Text>
                          </Box>
                        </Flex>
                      </Card.Section>
                    )}
                  </Card>
                );
              })}
            </ScrollArea>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>

      {/* <Modal
        opened={opened}
        onClose={close}
        title="최근 화물 불러오기"
        size="lg"
        scrollAreaComponent={ScrollArea.Autosize}
      ></Modal> */}
    </>
  );
}
